import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { IconButton, Tooltip } from "@mui/material";
import HideTableIcon from "../../components/ShowHideTableIcon/Hide";
import ShowTableIcon from "../../components/ShowHideTableIcon/Show";
import InfoIcon from "../../components/InfoIcon";
import SortedIcon from "../../components/SortTableIcon/Sorted";
import UnsortedIcon from "../../components/SortTableIcon/Unsorted";
import TipBox from "../../components/TipBox";

const TableHeader = React.memo(
  ({ headerGroups, toggleColumnVisibility, hiddenColumns }) => {
    const { t } = useTranslation();

    const renderColumnContent = (column) => {
      if (column.id === "expander") {
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {column.render("Header")}
          </div>
        );
      }

      return (
        <>
          {column.tipHeader && column.tipBody && (
            <TipBox title={column.tipHeader} message={column.tipBody}>
              <div>
                <IconButton
                  size="small"
                  style={{
                    position: "absolute",
                    top: "-17px",
                    left:
                      (column.canSort || column.canGroupBy) &&
                      !hiddenColumns.includes(column.id)
                        ? "8px"
                        : "50%",
                    transform:
                      column.canSort || column.canGroupBy
                        ? "none"
                        : "translateX(-50%)",
                  }}
                >
                  <InfoIcon />
                </IconButton>
              </div>
            </TipBox>
          )}
          {!hiddenColumns.includes(column.id) && column.canGroupBy && (
            <TipBox title={column.isGrouped ? "Ungroup" : "Group"}>
              <div>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    column.toggleGroupBy();
                  }}
                  style={{
                    position: "absolute",
                    top: "-17px",
                    right: column.canSort ? "32px" : "8px",
                  }}
                >
                  {column.isGrouped ? "📊" : "⊕"}
                </IconButton>
              </div>
            </TipBox>
          )}
          {!hiddenColumns.includes(column.id) && column.canSort && (
            <TipBox title="Sort">
              <div>
                <IconButton
                  size="small"
                  {...column.getSortByToggleProps()}
                  style={{
                    position: "absolute",
                    top: "-17px",
                    right: "8px",
                  }}
                >
                  {column.isSorted ? <SortedIcon /> : <UnsortedIcon />}
                </IconButton>
              </div>
            </TipBox>
          )}
          {column.canHide && (
            <TipBox
              title={
                hiddenColumns.includes(column.id)
                  ? "Show Column"
                  : "Hide Column"
              }
            >
              <div>
                <IconButton
                  size="small"
                  style={{
                    position: "absolute",
                    top: "-17px",
                    left:
                      !hiddenColumns.includes(column.id) && !column.tipBody
                        ? "8px"
                        : !hiddenColumns.includes(column.id) && column.tipBody
                        ? "32%"
                        : "0",
                  }}
                  onClick={() => toggleColumnVisibility(column.id)}
                >
                  {hiddenColumns.includes(column.id) ? (
                    <HideTableIcon />
                  ) : (
                    <ShowTableIcon />
                  )}
                </IconButton>
              </div>
            </TipBox>
          )}
          {!hiddenColumns.includes(column.id) && (
            <span
              style={{
                paddingTop: "5px",
                overflowWrap: "anywhere",
                fontWeight: column.isGrouped ? "800" : "bold",
                color: "#193666",
              }}
            >
              {typeof column.render === "function"
                ? column.render("header")
                : column.header}
              {column.isGrouped ? " (Grouped)" : ""}
            </span>
          )}
        </>
      );
    };

    return (
      <thead>
        {headerGroups.map((headerGroup) => (
          <tr
            className="customtableheader"
            {...headerGroup.getHeaderGroupProps()}
          >
            {headerGroup.headers.map((column) => (
              <th
                {...column.getHeaderProps([
                  {
                    ...column.getSortByToggleProps({ title: undefined }),
                    ...(column.getGroupByToggleProps
                      ? column.getGroupByToggleProps()
                      : {}),
                  },
                ])}
                className={
                  column.type !== "spacer" && column.id !== "actions"
                    ? "td-with-background"
                    : ""
                }
                style={{
                  width:
                    column.type === "spacer"
                      ? column.width
                      : hiddenColumns.includes(column.id)
                      ? "30px"
                      : column.width || "auto",
                  padding:
                    column.type === "spacer"
                      ? "0"
                      : hiddenColumns.includes(column.id)
                      ? "0"
                      : "8px",
                  textAlign: "center",
                  position: "relative",
                }}
              >
                {hiddenColumns.includes(column.id) && (
                  <div className="hidden-column-icon">
                    <TipBox
                      customStyle={{ margin: "0 auto" }}
                      title={t(column.render("header"))}
                    >
                      <div>
                        <IconButton size="small">
                          <InfoIcon />
                        </IconButton>
                      </div>
                    </TipBox>
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  {renderColumnContent(column)}
                </div>
              </th>
            ))}
          </tr>
        ))}
      </thead>
    );
  }
);

TableHeader.propTypes = {
  headerGroups: PropTypes.arrayOf(PropTypes.object).isRequired,
  toggleColumnVisibility: PropTypes.func.isRequired,
  hiddenColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default TableHeader;
